// Platform (superadmin only): workshops on Tallerza, their cars per month
// (the billing unit, from talleres/{t}/uso), their commercial record (plan,
// status, payment) and onboarding a new one with crearTaller (workshop,
// default config and its first admin). Workshops in pilot also show their
// activation (activacionPilotos: totals only, never a plate).
import { zodResolver } from '@hookform/resolvers/zod';
import { formatearRut } from '@taller/shared';
import { useMutacionUnica } from '@/lib/mutacionUnica';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Building2, Camera, Copy, Download, FileText, Mail, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  aCrearTaller,
  esquemaNuevoTaller,
  sugerirIdTaller,
  type NuevoTaller,
} from '@/admin/esquemas';
import { callables, mensajeError } from '@/datos/acciones';
import { useFichasComerciales, useTalleres, useUsoTalleres } from '@/datos/consultas';
import { aFecha, aMillis, type TallerDoc } from '@/datos/tipos';
import { exigir } from '@/lib/guardias';
import { permisos } from '@/lib/sesion';
import { diaIso, fechaConAnio } from '@/lib/tiempo';
import { Activacion, useActivacionPilotos } from '@/plataforma/Activacion';
import { enPiloto, vistaActivacion } from '@/plataforma/activacion';
import {
  ETIQUETAS_ESTADO_COMERCIAL,
  ETIQUETAS_ESTADO_PAGO,
  INFO_PLANES,
  TONO_ESTADO_COMERCIAL,
  TONO_PAGO,
  type FichaDoc,
} from '@/plataforma/comercial';
import { DialogoEnviarCorreo } from '@/plataforma/DialogoEnviarCorreo';
import { DialogoExportarTaller } from '@/plataforma/DialogoExportarTaller';
import { UsoTaller } from '@/plataforma/UsoTaller';
import { mesesDeUso } from '@/plataforma/uso';
import { resumenTerminos } from '@/terminos/terminos';
import { ErrorCarga, Esqueleto, Insignia, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { Campo, Entrada } from '@/ui/campo';
import { Dialogo, DialogoContenido, DialogoCuerpo, DialogoPie } from '@/ui/dialogo';
import { cn } from '@/ui/cn';
import { Contenedor } from '@/ui/pagina';

export const Route = createFileRoute('/_app/plataforma/talleres')({
  beforeLoad: exigir(permisos.plataforma),
  component: Pagina,
});

function Pagina() {
  const q = useTalleres();
  const fichas = useFichasComerciales();
  const fichaDe = useMemo(
    () => new Map((fichas.data ?? []).map((f) => [f.id, f] as const)),
    [fichas.data],
  );
  const [nuevo, setNuevo] = useState({ abierto: false, n: 0 });
  const [exportar, setExportar] = useState<{ id: string; nombre: string } | null>(null);
  const [correo, setCorreo] = useState({ abierto: false, n: 0, tallerId: '' });
  const talleres = useMemo(
    () =>
      [...(q.data ?? [])].sort((a, b) => (aMillis(b.creadoEn) ?? 0) - (aMillis(a.creadoEn) ?? 0)),
    [q.data],
  );
  // Computed once per visit: a page left open past midnight keeps its months.
  const [meses] = useState(() => mesesDeUso());
  const ids = useMemo(() => talleres.map((t) => t.id), [talleres]);
  const uso = useUsoTalleres(ids, meses.actual, meses.anterior);
  const [hoy] = useState(() => diaIso());
  const hayPilotos = useMemo(() => (fichas.data ?? []).some(enPiloto), [fichas.data]);
  const activacion = useActivacionPilotos(hayPilotos);
  const crear = (
    <Boton variante="primario" onClick={() => setNuevo((x) => ({ abierto: true, n: x.n + 1 }))}>
      <Plus /> Nuevo taller
    </Boton>
  );
  return (
    <Contenedor>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="titulo text-[24px] leading-tight">Talleres</h1>
          <p className="text-[15px] text-text-2">
            {q.data
              ? `${talleres.length} ${talleres.length === 1 ? 'taller' : 'talleres'} en la plataforma`
              : 'Cargando…'}
          </p>
        </div>
        {crear}
      </div>
      {q.isError ? (
        <ErrorCarga error={q.error} />
      ) : !q.data ? (
        <Tarjeta className="flex flex-col gap-3 p-5" aria-busy="true">
          <Esqueleto className="h-14" />
          <Esqueleto className="h-14" />
        </Tarjeta>
      ) : talleres.length === 0 ? (
        <Tarjeta>
          <Vacio icono={<Building2 />} titulo="Aún no hay talleres" accion={crear}>
            Crea el primero: queda con la configuración por defecto y su administrador puede
            ingresar de inmediato.
          </Vacio>
        </Tarjeta>
      ) : (
        <Tarjeta className="overflow-hidden">
          <ul className="divide-y divide-border">
            {talleres.map((t) => {
              const creado = aFecha(t.creadoEn);
              const ficha = fichaDe.get(t.id);
              return (
                <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-4">
                  <span className="grid size-10 flex-none place-items-center rounded-[12px] bg-primary-soft text-primary-text">
                    <Building2 className="size-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-[16rem] flex-1">
                    <p className="font-bold">{t.nombre}</p>
                    <p className="text-[13px] text-text-2">
                      <span className="font-mono">{t.id}</span>
                      {creado ? ` · creado el ${fechaConAnio(creado)}` : ''}
                    </p>
                    <EstadoTerminos terminos={t.terminos} />
                    <ResumenComercial ficha={ficha} cargando={!fichas.data} />
                  </div>
                  <UsoTaller
                    uso={uso.get(t.id)?.data}
                    error={uso.get(t.id)?.error ?? false}
                    actual={meses.actual}
                    anterior={meses.anterior}
                  />
                  {t.activo === false ? (
                    <Insignia tono="peligro" punto>
                      Dado de baja
                    </Insignia>
                  ) : t.soloLectura === true ? (
                    <Insignia tono="alerta" punto>
                      Suspendido
                    </Insignia>
                  ) : (
                    <Insignia tono="exito" punto>
                      Activo
                    </Insignia>
                  )}
                  <div className="flex basis-full flex-wrap gap-2 sm:pl-14">
                    <Boton asChild tamano="sm" className="w-full sm:w-auto">
                      <Link
                        to="/plataforma/talleres/$tallerId/comercial"
                        params={{ tallerId: t.id }}
                        aria-label={`Ficha comercial de ${t.nombre}`}
                      >
                        <FileText /> Ficha comercial
                      </Link>
                    </Boton>
                    <Boton asChild tamano="sm" className="w-full sm:w-auto">
                      <Link
                        to="/plataforma/talleres/$tallerId/fotos"
                        params={{ tallerId: t.id }}
                        aria-label={`Configurar fotos de ${t.nombre}`}
                      >
                        <Camera /> Configurar fotos
                      </Link>
                    </Boton>
                    <Boton
                      tamano="sm"
                      className="w-full sm:w-auto"
                      aria-label={`Exportar datos de ${t.nombre}`}
                      onClick={() => setExportar({ id: t.id, nombre: t.nombre })}
                    >
                      <Download /> Exportar datos
                    </Boton>
                    {t.activo !== false && (
                      <Boton
                        tamano="sm"
                        className="w-full sm:w-auto"
                        aria-label={`Enviar un correo a ${t.nombre}`}
                        onClick={() =>
                          setCorreo((x) => ({ abierto: true, n: x.n + 1, tallerId: t.id }))
                        }
                      >
                        <Mail /> Enviar correo
                      </Boton>
                    )}
                  </div>
                  {enPiloto(ficha) && (
                    <div className="basis-full sm:pl-14">
                      <Activacion
                        nombre={t.nombre}
                        vista={vistaActivacion(ficha, activacion.data?.get(t.id), hoy)}
                        cargando={activacion.isPending}
                        error={activacion.isError}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Tarjeta>
      )}
      <p className="text-[13px] text-text-2">
        Un auto es cada patente con al menos una inspección firmada en el mes (hora de Chile) y se
        cuenta una vez al mes; el plan sugerido es el más barato que lo cubre. Cada taller está
        aislado: desde aquí solo se ven esos totales, se lleva su ficha comercial (que el taller no
        ve) y se ajusta la calidad de las fotos; no se leen inspecciones ni patentes de ningún
        taller. La única excepción es exportar sus datos (por ejemplo, para entregárselos al
        terminar un piloto), que queda registrada en la auditoría del taller.
      </p>
      <DialogoExportarTaller taller={exportar} onCerrar={() => setExportar(null)} />
      <DialogoEnviarCorreo
        key={correo.n}
        abierto={correo.abierto}
        inicial={correo.tallerId ? { destino: { tipo: 'taller', tallerId: correo.tallerId } } : {}}
        onCambio={(v) => setCorreo((x) => ({ ...x, abierto: v }))}
      />
      <DialogoNuevoTaller
        key={nuevo.n}
        abierto={nuevo.abierto}
        onCambio={(v) => setNuevo((x) => ({ ...x, abierto: v }))}
      />
    </Contenedor>
  );
}

/** Terms version the workshop accepted and when (aceptarTerminos), or that it is missing. */
function EstadoTerminos({ terminos }: { terminos: TallerDoc['terminos'] }) {
  const r = resumenTerminos(terminos);
  return (
    <p className={cn('text-[13px]', r.vigente ? 'text-text-2' : 'font-bold text-ray-texto')}>
      {r.texto}
    </p>
  );
}

/** Plan, commercial status and payment of a workshop, or that it has no record yet. */
function ResumenComercial({ ficha, cargando }: { ficha: FichaDoc | undefined; cargando: boolean }) {
  if (cargando) return null;
  if (!ficha) return <p className="mt-1 text-[13px] text-text-2">Sin ficha comercial</p>;
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <Insignia>{INFO_PLANES[ficha.plan]?.nombre ?? ficha.plan}</Insignia>
      <Insignia tono={TONO_ESTADO_COMERCIAL[ficha.estado] ?? 'neutro'}>
        {ETIQUETAS_ESTADO_COMERCIAL[ficha.estado] ?? ficha.estado}
      </Insignia>
      {ficha.pago !== 'al_dia' && (
        <Insignia tono={TONO_PAGO[ficha.pago] ?? 'neutro'} punto>
          Pago {(ETIQUETAS_ESTADO_PAGO[ficha.pago] ?? ficha.pago).toLowerCase()}
        </Insignia>
      )}
    </p>
  );
}

function DialogoNuevoTaller({
  abierto,
  onCambio,
}: {
  abierto: boolean;
  onCambio: (v: boolean) => void;
}) {
  const [creado, setCreado] = useState<NuevoTaller | null>(null);
  const [idTocado, setIdTocado] = useState(false);
  const form = useForm<NuevoTaller>({
    resolver: zodResolver(esquemaNuevoTaller),
    defaultValues: {
      tallerId: '',
      nombre: '',
      razonSocial: '',
      rut: '',
      direccion: '',
      adminNombre: '',
      adminEmail: '',
      adminPassword: '',
    },
  });
  const { errors } = form.formState;
  const guardar = useMutacionUnica({
    mutationFn: (v: NuevoTaller) => callables.crearTaller(aCrearTaller(v)),
    onSuccess: (_, v) => {
      toast.success(`${v.nombre} está listo`);
      setCreado(v);
    },
    onError: (e) => {
      const msg = mensajeError(e);
      if (/ya existe/i.test(msg)) form.setError('tallerId', { message: 'Ese id ya está en uso.' });
      toast.error('No se pudo crear el taller', { description: msg });
    },
  });
  const r = form.register;

  return (
    <Dialogo open={abierto} onOpenChange={(v) => !guardar.isPending && onCambio(v)}>
      <DialogoContenido
        titulo={creado ? 'Taller creado' : 'Nuevo taller'}
        descripcion={
          creado
            ? 'Entrega estos datos al administrador del taller en persona.'
            : 'Se crea con las 9 fases, cupos y catálogo por defecto. Su administrador los ajusta después.'
        }
        ancho="md"
      >
        {creado ? (
          <>
            <DialogoCuerpo className="flex flex-col gap-3 text-[14px]">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-[12px] bg-surface-muted p-4">
                <dt className="text-text-2">Taller</dt>
                <dd className="font-bold">{creado.nombre}</dd>
                <dt className="text-text-2">Ingreso</dt>
                <dd>{window.location.origin}</dd>
                <dt className="text-text-2">Correo</dt>
                <dd className="break-all">{creado.adminEmail.toLowerCase()}</dd>
                <dt className="text-text-2">Contraseña</dt>
                <dd className="font-mono">{creado.adminPassword}</dd>
              </dl>
              <p className="text-text-2">
                Pídele que la cambie al ingresar. Esta contraseña no se vuelve a mostrar.
              </p>
            </DialogoCuerpo>
            <DialogoPie>
              <Boton
                onClick={() =>
                  void navigator.clipboard
                    .writeText(
                      `${creado.nombre}\n${window.location.origin}\n${creado.adminEmail.toLowerCase()}\n${creado.adminPassword}`,
                    )
                    .then(() => toast.success('Datos copiados'))
                }
              >
                <Copy /> Copiar datos
              </Boton>
              <Boton variante="primario" onClick={() => onCambio(false)}>
                Listo
              </Boton>
            </DialogoPie>
          </>
        ) : (
          <form
            onSubmit={form.handleSubmit((v) => guardar.mutate(v))}
            className="flex min-h-0 flex-1 flex-col"
            noValidate
          >
            <DialogoCuerpo className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo etiqueta="Nombre del taller" error={errors.nombre?.message}>
                  {(p) => (
                    <Entrada
                      {...p}
                      {...r('nombre', {
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          if (!idTocado) form.setValue('tallerId', sugerirIdTaller(e.target.value));
                        },
                      })}
                      autoFocus
                    />
                  )}
                </Campo>
                <Campo
                  etiqueta="Id del taller"
                  error={errors.tallerId?.message}
                  ayuda="Permanente. Solo minúsculas, números y guiones."
                >
                  {(p) => (
                    <Entrada
                      {...p}
                      {...r('tallerId', { onChange: () => setIdTocado(true) })}
                      className="font-mono"
                    />
                  )}
                </Campo>
                <Campo etiqueta="Razón social" error={errors.razonSocial?.message}>
                  {(p) => <Entrada {...p} {...r('razonSocial')} />}
                </Campo>
                <Campo etiqueta="RUT" error={errors.rut?.message}>
                  {(p) => (
                    <Entrada
                      {...p}
                      {...r('rut', {
                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                          const v = e.target.value.trim();
                          if (v.length > 2) form.setValue('rut', formatearRut(v));
                        },
                      })}
                      placeholder="76.123.456-7"
                    />
                  )}
                </Campo>
              </div>
              <Campo etiqueta="Dirección" error={errors.direccion?.message}>
                {(p) => <Entrada {...p} {...r('direccion')} />}
              </Campo>
              <fieldset className="flex flex-col gap-4 border-t border-border pt-4">
                <legend className="sr-only">Primer administrador</legend>
                <p className="-mb-1 text-[13px] font-bold text-text-long">Primer administrador</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Campo etiqueta="Nombre" error={errors.adminNombre?.message}>
                    {(p) => <Entrada {...p} {...r('adminNombre')} />}
                  </Campo>
                  <Campo etiqueta="Correo" error={errors.adminEmail?.message}>
                    {(p) => <Entrada {...p} {...r('adminEmail')} type="email" />}
                  </Campo>
                </div>
                <Campo
                  etiqueta="Contraseña inicial"
                  error={errors.adminPassword?.message}
                  ayuda="Mínimo 8 caracteres."
                >
                  {(p) => (
                    <Entrada
                      {...p}
                      {...r('adminPassword')}
                      type="text"
                      autoComplete="new-password"
                      className="font-mono"
                    />
                  )}
                </Campo>
              </fieldset>
            </DialogoCuerpo>
            <DialogoPie>
              <Boton onClick={() => onCambio(false)} disabled={guardar.isPending}>
                Cancelar
              </Boton>
              <Boton type="submit" variante="primario" cargando={guardar.isPending}>
                Crear taller
              </Boton>
            </DialogoPie>
          </form>
        )}
      </DialogoContenido>
    </Dialogo>
  );
}
